/* =========================================================
   NEWBANYOMDA — CART ENGINE
   Version: 2.1-RECOMMENDATION-SAFE

   ARCHITECTURE
   ---------------------------------------------------------
   - AJAX add to cart
   - NewBanyomda / editorial product cards
   - BYD collection product cards
   - Native FoxTheme CartDrawer refresh
   - Native cart recommendation refresh
   - Cart count synchronization via native theme
   - Dynamic DOM safe
   - Loading / success / error states
   - Duplicate click protection
   - Existing CartDrawer system remains untouched
   ========================================================= */

(() => {
  'use strict';


  /* =========================================================
     00 — SINGLE INSTANCE GUARD
     ========================================================= */

  if (window.__NewBanyomdaCartEngineLoaded) {
    return;
  }

  window.__NewBanyomdaCartEngineLoaded = true;


  /* =========================================================
     01 — CONFIG
     ========================================================= */

  const CONFIG = {

    selectors: {

      /*
       * NewBanyomda özel ürün kartları
       */
      addButton:
        [
          '[data-newbanyomda-add]',
          '.byd-grid-card__btn[data-variant-id]'
        ].join(','),

    },


    classes: {

      loading:
        'is-loading',

      success:
        'is-added',

      error:
        'has-error'

    },


    quantity:
      1,


    successDuration:
      1200,


    errorDuration:
      2200

  };


  /* =========================================================
     02 — ROUTES
     Locale-aware Shopify Ajax routes.
     ========================================================= */

  const ROOT =
    window.Shopify?.routes?.root || '/';


  const ROUTES = {

    add:
      `${ROOT}cart/add.js`

  };


  /* =========================================================
     03 — UTILITIES
     ========================================================= */

  const Utils = {

    /*
     * Safely parse JSON.
     */
    async parseJSON(response) {

      const text =
        await response.text();

      if (!text) {
        return {};
      }

      try {

        return JSON.parse(text);

      } catch (error) {

        throw new Error(
          'Sunucudan geçersiz bir yanıt alındı.'
        );

      }

    },


    /*
     * Check whether element still exists
     * in current document.
     */
    isConnected(element) {

      return Boolean(
        element &&
        element.isConnected
      );

    },


    /*
     * Wait one animation frame.
     *
     * Gives Shopify / browser DOM one frame
     * before native CartDrawer refresh starts.
     */
    nextFrame() {

      return new Promise(
        (resolve) => {

          requestAnimationFrame(
            () => resolve()
          );

        }
      );

    }

  };


  /* =========================================================
     04 — CART ENGINE
     ========================================================= */

  const NewBanyomdaCart = {

    /*
     * Prevent duplicate clicks on the same button
     * without globally blocking every product card.
     */
    pendingButtons:
      new WeakSet(),

    /*
     * Serialize custom cart mutations. Shopify's Cart API is stateful;
     * allowing two independent section-rendering responses to race can
     * cause an older response to overwrite a newer cart state. Each click
     * still gets its own loading state, but cart mutations commit in order.
     */
    mutationQueue:
      Promise.resolve(),

    /* =======================================================
       ADD PRODUCT
       ======================================================= */

    add(
      variantId,
      quantity = CONFIG.quantity,
      button = null
    ) {
      const operation = () =>
        this._add(variantId, quantity, button);

      const queued =
        this.mutationQueue.then(operation, operation);

      this.mutationQueue = queued.catch(() => {});
      return queued;
    },

    async _add(
      variantId,
      quantity = CONFIG.quantity,
      button = null
    ) {

      const normalizedVariantId =
        Number(variantId);

      const normalizedQuantity =
        Number(quantity);


      /* -----------------------------------------------------
         VALIDATION
         ----------------------------------------------------- */

      if (
        !Number.isFinite(
          normalizedVariantId
        ) ||
        normalizedVariantId <= 0
      ) {

        console.error(
          '[NewBanyomda Cart] Invalid variant ID:',
          variantId
        );

        return {
          success: false,
          error: new Error(
            'Geçersiz ürün varyantı.'
          )
        };

      }


      if (
        !Number.isFinite(
          normalizedQuantity
        ) ||
        normalizedQuantity <= 0
      ) {

        console.error(
          '[NewBanyomda Cart] Invalid quantity:',
          quantity
        );

        return {
          success: false,
          error: new Error(
            'Geçersiz ürün adedi.'
          )
        };

      }


      /* -----------------------------------------------------
         DUPLICATE CLICK PROTECTION
         ----------------------------------------------------- */

      if (
        button &&
        this.pendingButtons.has(button)
      ) {

        return {
          success: false,
          ignored: true
        };

      }


      if (button) {

        this.pendingButtons.add(
          button
        );

        this.setLoading(
          button,
          true
        );

      }


      try {

        /* ---------------------------------------------------
           SHOPIFY AJAX ADD
           --------------------------------------------------- */

        /* ---------------------------------------------------
           MATCH SHOPIFY'S NATIVE PRODUCT-FORM FLOW

           Ask every active cart-aware component which section it needs.
           Shopify then returns those sections in the same cart mutation
           response, avoiding a second Section Rendering request and the
           race condition that previously existed between /cart/add.js and
           the later cart drawer refresh.
           --------------------------------------------------- */
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle },
          })
        );

        const uniqueSections = [...new Set(
          sectionsToBundle.filter(Boolean)
        )];

        const response =
          await fetch(
            ROUTES.add,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',

                'Accept':
                  'application/json'
              },

              credentials:
                'same-origin',

              body:
                JSON.stringify({
                  items: [
                    {
                      id:
                        normalizedVariantId,

                      quantity:
                        normalizedQuantity
                    }
                  ],
                  ...(uniqueSections.length > 0
                    ? {
                        sections: uniqueSections.join(','),
                        sections_url: window.location.pathname,
                      }
                    : {})
                })
            }
          );


        const data =
          await Utils.parseJSON(
            response
          );


        /* ---------------------------------------------------
           HTTP ERROR
           --------------------------------------------------- */

        if (!response.ok) {

          throw new Error(
            data?.description ||
            data?.message ||
            'Ürün sepete eklenemedi.'
          );

        }


        /* ---------------------------------------------------
           NATIVE CART STATE COMMIT

           The native FoxTheme ProductForm follows this exact model:
           1. /cart/add.js returns bundled section HTML.
           2. /cart.js returns the authoritative cart state.
           3. PUB_SUB_EVENTS.cartUpdate updates CartItems/cart count/etc.
           4. The drawer is opened only after the DOM has the new state.

           This keeps desktop and mobile on one cart update contract.
           --------------------------------------------------- */
        const cartResponse = await fetch(
          `${FoxTheme.routes.cart_url}.js`,
          {
            ...FoxTheme.utils.fetchConfig('json', 'GET'),
            credentials: 'same-origin',
            cache: 'no-store',
          }
        );

        if (!cartResponse.ok) {
          throw new Error(`Cart state request failed: ${cartResponse.status}`);
        }

        const cartJson = await cartResponse.json();
        cartJson.sections = data.sections || {};

        FoxTheme.pubsub.publish(
          FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          {
            cart: cartJson,
            source: 'newbanyomda',
          }
        );

        await Utils.nextFrame();

        const cartDrawer =
          document.querySelector('cart-drawer');

        if (cartDrawer?.show) {
          cartDrawer.show(button);
        }


        /* ---------------------------------------------------
           NEWBANYOMDA COMPATIBILITY EVENT
           --------------------------------------------------- */

        document.dispatchEvent(

          new CustomEvent(
            'newbanyomda:cart:updated',
            {
              bubbles: true,

              detail: {
                product:
                  data
              }

            }
          )

        );


        /* ---------------------------------------------------
           THEME COMPATIBILITY EVENT
           --------------------------------------------------- */

        document.dispatchEvent(

          new CustomEvent(
            'cart:updated',
            {
              bubbles: true,

              detail: {
                product:
                  data
              }

            }
          )

        );


        /* ---------------------------------------------------
           BUTTON SUCCESS
           --------------------------------------------------- */

        if (button) {

          this.setSuccess(
            button
          );

        }


        return {

          success:
            true,

          product:
            data

        };


      } catch (error) {

        console.error(
          '[NewBanyomda Cart] Add failed:',
          error
        );


        if (button) {

          this.setError(
            button,
            error?.message
          );

        }


        return {

          success:
            false,

          error

        };


      } finally {

        if (button) {

          window.setTimeout(
            () => {

              if (
                !Utils.isConnected(
                  button
                )
              ) {

                return;

              }


              this.setLoading(
                button,
                false
              );


              this.setSuccess(
                button,
                false
              );


              this.setError(
                button,
                false
              );


              this.pendingButtons.delete(
                button
              );

            },
            CONFIG.successDuration
          );

        }

      }

    },


    /* =======================================================
       BUTTON — LOADING
       ======================================================= */

    setLoading(
      button,
      state
    ) {

      if (!button) {
        return;
      }


      button.classList.toggle(
        CONFIG.classes.loading,
        Boolean(state)
      );


      button.disabled =
        Boolean(state);


      if (state) {

        button.setAttribute(
          'aria-busy',
          'true'
        );


        const text =
          button.querySelector(
            '.btn__text, span'
          );


        if (
          !button.dataset.originalText
        ) {

          button.dataset.originalText =
            text?.textContent?.trim() ||
            button.textContent.trim();

        }


        if (
          !button.dataset.originalAriaLabel
        ) {

          button.dataset.originalAriaLabel =
            button.getAttribute('aria-label') || '';

        }


        if (text) {

          text.textContent =
            'Ekleniyor…';

        }

      } else {

        button.removeAttribute(
          'aria-busy'
        );


        const text =
          button.querySelector(
            '.btn__text, span'
          );


        /*
         * Success / error state kendi metnini
         * yönetiyorsa üzerine yazma.
         */
        if (
          !button.classList.contains(
            CONFIG.classes.success
          ) &&
          !button.classList.contains(
            CONFIG.classes.error
          ) &&
          text &&
          button.dataset.originalText
        ) {

          text.textContent =
            button.dataset.originalText;

        } else if (button.dataset.originalAriaLabel) {

          button.setAttribute(
            'aria-label',
            button.dataset.originalAriaLabel
          );

        }

      }

    },


    /* =======================================================
       BUTTON — SUCCESS
       ======================================================= */

    setSuccess(
      button,
      state = true
    ) {

      if (!button) {
        return;
      }


      button.classList.toggle(
        CONFIG.classes.success,
        Boolean(state)
      );


      const text =
        button.querySelector(
          '.btn__text, span'
        );


      if (state) {

        if (text) {

          text.textContent =
            'Sepete Eklendi';

        } else {

          if (!button.dataset.originalAriaLabel) {

            button.dataset.originalAriaLabel =
              button.getAttribute('aria-label') || '';

          }

          button.setAttribute(
            'aria-label',
            'Sepete Eklendi'
          );

        }


        button.setAttribute(
          'aria-live',
          'polite'
        );


        window.setTimeout(
          () => {

            if (
              !Utils.isConnected(
                button
              )
            ) {

              return;

            }


            this.setSuccess(
              button,
              false
            );

          },
          CONFIG.successDuration
        );


      } else {

        button.removeAttribute(
          'aria-live'
        );


        if (text) {

          text.textContent =
            button.dataset.originalText ||
            'Sepete Ekle';

        } else if (button.dataset.originalAriaLabel) {

          button.setAttribute(
            'aria-label',
            button.dataset.originalAriaLabel
          );

        }

      }

    },


    /* =======================================================
       BUTTON — ERROR
       ======================================================= */

    setError(
      button,
      message,
      state = true
    ) {

      if (!button) {
        return;
      }


      button.classList.toggle(
        CONFIG.classes.error,
        Boolean(state)
      );


      const text =
        button.querySelector(
          '.btn__text, span'
        );


      if (state) {

        if (text) {

          text.textContent =
            'Tekrar Dene';

        }


        if (message) {

          if (!button.dataset.originalAriaLabel) {

            button.dataset.originalAriaLabel =
              button.getAttribute('aria-label') || '';

          }

          button.setAttribute(
            'aria-label',
            message
          );

        }


        window.setTimeout(
          () => {

            if (
              !Utils.isConnected(
                button
              )
            ) {

              return;

            }


            this.setError(
              button,
              null,
              false
            );

          },
          CONFIG.errorDuration
        );


      } else {

        button.removeAttribute(
          'aria-label'
        );


        if (text) {

          text.textContent =
            button.dataset.originalText ||
            'Sepete Ekle';

        } else if (button.dataset.originalAriaLabel) {

          button.setAttribute(
            'aria-label',
            button.dataset.originalAriaLabel
          );

        }

      }

    }

  };


  /* =========================================================
     05 — EVENT BRIDGE
     =========================================================

     capture:true kullanıyoruz.

     Bunun amacı:

     .byd-grid-card
        ↓
     Shopify product-form submit
        ↓
     native form handler

     yerine:

     .byd-grid-card
        ↓
     NewBanyomda Cart Engine
        ↓
     /cart/add.js + bundled sections
        ↓
     native cartUpdate pub/sub
        ↓
     Native CartDrawer

     akışını garanti etmek.
     ========================================================= */

  document.addEventListener(

    'click',

    (event) => {

      const target =
        event.target;

      if (
        !target ||
        typeof target.closest !==
          'function'
      ) {

        return;

      }


      const button =
        target.closest(
          CONFIG.selectors.addButton
        );


      if (!button) {
        return;
      }


      /*
       * Native form / eski delegated
       * add-to-cart handler'larını engelle.
       */
      event.preventDefault();

      event.stopPropagation();


      const variantId =
        button.getAttribute(
          'data-variant-id'
        );


      if (!variantId) {

        console.error(
          '[NewBanyomda Cart] Variant ID missing.'
        );

        return;

      }


      /*
       * Disabled button'a tekrar basılmasını önle.
       */
      if (button.disabled) {
        return;
      }


      NewBanyomdaCart.add(
        variantId,
        CONFIG.quantity,
        button
      );

    },

    {
      capture:
        true
    }

  );


  /* =========================================================
     06 — PUBLIC API
     ========================================================= */

  window.NewBanyomda =
    window.NewBanyomda || {};


  window.NewBanyomda.Cart =
    NewBanyomdaCart;


  /* =========================================================
     07 — READY SIGNAL
     ========================================================= */

  document.dispatchEvent(

    new CustomEvent(
      'newbanyomda:cart:ready',
      {
        bubbles:
          true
      }
    )

  );


  /* =========================================================
     08 — DEVELOPMENT SIGNAL
     ========================================================= */

  console.info(
    '[NewBanyomda] Cart Engine 2.0 recommendation bridge ready.'
  );


})();